const HOUR_MS = 60 * 60 * 1000;

interface Sensor {
  SensorType?: string;
  LatestSensorActivities?: Array<{ LastActivity: string }>;
  DataLastRecorded?: string;
}

/**
 * Derive the three-state status from raw sensor row data.
 *
 * @param {Sensor} sensor  - processed sensor row
 * @returns {'Online'|'Degraded'|'Offline'}
 */

export function deriveSensorStatus(
  sensor: Sensor,
): 'Online' | 'Degraded' | 'Offline' {
  // GatewaySensor only uses DataLastRecorded (no SensorActivities)
  if (sensor?.SensorType === 'GatewaySensor') {
    if (!sensor.DataLastRecorded) return 'Offline';

    const dataAge = Date.now() - new Date(sensor.DataLastRecorded).getTime();
    return dataAge > HOUR_MS ? 'Offline' : 'Online';
  }

  const connections = sensor?.LatestSensorActivities ?? [];

  if (connections.length === 0) return 'Offline';

  const latestActivity = connections.reduce((latest, c) => {
    const t = new Date(c.LastActivity).getTime();
    return t > latest ? t : latest;
  }, 0);

  const activityAge = Date.now() - latestActivity;
  if (activityAge > HOUR_MS) return 'Offline';

  // Online — but check if data is stale
  const dataLastRecorded = sensor.DataLastRecorded
    ? new Date(sensor.DataLastRecorded).getTime()
    : null;

  if (dataLastRecorded !== null) {
    const deviation = Math.abs(latestActivity - dataLastRecorded);
    if (deviation > HOUR_MS) return 'Degraded';
  }

  return 'Online';
}
